// @file Dash accounts pinia store
import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import getCsrfToken from '@@/bits/csrf_token'
import device from '@@/bits/device'
import { captureNonNetworkFetchError } from '@@/bits/error_tracker'
import { greet } from '@@/bits/greeting'
import { __ } from '@@/bits/intl'
import { getOwnerOnboardingDataByTask } from '@@/bits/libraries_helper'
import { navigateTo } from '@@/bits/location'
import { NATIVE_HOST } from '@@/bits/url'
import { displayNameForUser } from '@@/bits/user_model'
import { LibraryType } from '@@/enums'
import { OzConfirmationDialogBoxButtonScheme } from '@@/library/v4/components/OzConfirmationDialogBox.vue'
import { useDashCollectionsStore } from '@@/pinia/dash_collections_store'
import { useDashStore } from '@@/pinia/dash_store'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { NATIVE_TENANT_ID, useUserAccountsStore } from '@@/pinia/user_accounts_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { fetchJson } from '@@/surface/api_fetch'
import type {
  AccountKey,
  AccountMenuItem,
  JsonApiData,
  JsonApiResponse,
  Library,
  LibraryId,
  LibraryMembership,
  OwnerOnboardingData,
  OwnerOnboardingTask,
  OwnerOnboardingTaskData,
  Tenant,
  TenantId,
  User,
  UserId,
  UserOptions,
} from '@@/types'
import type { NonCurrentTenantAccountsMenuItem } from '@@/types/dash'
import { MobilePage } from '@@/types/dash'
import type { JsonAPIResource } from '@padlet/arvo'
import { HTTPMethod } from '@padlet/fetch'
import { isEqual, sortBy } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed, ref, toRaw } from 'vue'

type LibraryIdToUserRoleMap = Record<LibraryId, string>
const DEFAULT_AVATAR = 'https://padlet.net/avatars/tenant_avatar.webp'

export const useDashAccountsStore = defineStore('dashAccountsStore', () => {
  const userAccountsStore = useUserAccountsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  const dashCollectionsStore = useDashCollectionsStore()
  const dashStore = useDashStore()
  const windowSizeStore = useWindowSizeStore()
  const personalPlansStore = usePersonalPlansStore()

  function genericFetchError(payload: { error: any; source: string }): void {
    captureNonNetworkFetchError(payload.error, { source: payload.source })
    globalSnackbarStore.genericFetchError()
  }

  /**
   * ==================== PERSONAL ACCOUNTS ====================
   */

  const currentUserOptions = ref<UserOptions>({})

  const user = computed((): User => userAccountsStore.user)
  const usersById = computed((): Record<UserId, User> => userAccountsStore.usersById)
  const tenantsById = computed((): Record<TenantId, Tenant> => userAccountsStore.tenantsById)
  const currentAccountKey = computed((): AccountKey | null => dashCollectionsStore.currentAccountKey)
  const isCurrentAccountLibrary = computed((): boolean => currentAccountKey.value?.type === 'library')
  const currentUser = computed((): User => userAccountsStore.currentUser)
  const canCreateWall = computed((): boolean => currentUser.value?.quota?.can_make)
  const isNativeAccount = computed((): boolean => currentUser.value?.tenant_id === NATIVE_TENANT_ID)
  const currentTenant = computed((): Tenant | null => userAccountsStore.currentTenant)
  const currentTenantName = computed((): string => currentTenant.value?.name ?? '')
  const currentTenantLogo = computed((): string =>
    currentTenant.value?.assets != null ? (currentTenant.value?.assets?.home_logo as string) : DEFAULT_AVATAR,
  )
  const isBackpack = computed((): boolean => currentTenant.value?.type === 'school')
  const isBriefcase = computed((): boolean => currentTenant.value?.type === 'business')
  const isStudent = computed((): boolean => currentUser.value.role === 'student')
  const userRolesByLibraryId = computed((): LibraryIdToUserRoleMap => {
    return userLibraryMemberships.value.reduce<LibraryIdToUserRoleMap>((accumulator, membership) => {
      accumulator[membership.libraryId] = membership.role
      return accumulator
    }, {})
  })
  const usersArray = computed((): User[] => userAccountsStore.usersArray)
  const accountNamesByUserId = computed((): Record<UserId, string> => {
    const hash: Record<UserId, string> = {}
    usersArray.value.forEach((user) => {
      const tenantId = user.tenant_id
      if (Number(tenantId) === NATIVE_TENANT_ID) {
        hash[user.id] = displayNameForUser(user)
      } else {
        hash[user.id] = tenantsById.value[tenantId]?.name
      }
    })
    return hash
  })
  const nonCurrentTenantAccountsMenuList = computed((): NonCurrentTenantAccountsMenuItem[] => {
    return usersArray.value
      .filter((user) => user.tenant_id !== currentTenant.value?.id)
      .map((user) => {
        const tenantId: TenantId = user.tenant_id
        const tenant: Tenant = tenantsById.value[tenantId]
        const tenantName: string = tenant !== undefined ? tenant.name : ''
        const tenantDomainName: string = tenant?.domain_name ?? NATIVE_HOST
        const isNative = tenantId === NATIVE_TENANT_ID
        const email = user.email
        const username = user.username as string
        const name = user.name as string
        const url = `https://${tenantDomainName}/dashboard`

        // if no uploaded tenant logo, logo is default padlet logo
        let logo = tenant?.assets != null ? (tenant.assets.home_logo as string) : DEFAULT_AVATAR
        if (isNative && user?.avatar !== undefined) logo = user.avatar

        return {
          name,
          username,
          email,
          logo,
          tenantName,
          tenantDomainName,
          isNative,
          url,
        }
      })
  })
  const accountsMenuList = computed((): AccountMenuItem[] => userAccountsStore.accountsMenuList)
  const transferableAccounts = computed<AccountMenuItem[]>(() =>
    accountsMenuList.value.filter((account) => !isEqual(account.key, toRaw(currentAccountKey.value))),
  )
  const dashNameString = computed((): string => {
    return __('Hi, %{userShortName}', {
      userShortName: currentUser.value?.short_name ?? currentUser.value?.username ?? '',
    })
  })
  const dashGreeting = computed((): string => greet(currentUser.value))

  function updateUserOptions(payload: { userOptions: UserOptions }): void {
    currentUserOptions.value = payload.userOptions
  }

  function refreshWallQuotas(payload?: { forceQuotaRefresh: boolean }): void {
    userAccountsStore.refreshWallQuotas(payload)
  }

  async function initialize(): Promise<void> {
    await fetchAccounts()
    void fetchAllLibraryOwnerOnboardingData()
  }

  async function fetchAccounts(params?: { forceQuotaRefresh: boolean | undefined }): Promise<void> {
    await userAccountsStore.fetchAccounts(params)
  }

  async function fetchAllLibraryOwnerOnboardingData(): Promise<void> {
    const promises: Array<Promise<void>> = []
    librariesArray.value.forEach((library) => {
      if (currentUser.value.id === library.ownerId) {
        promises.push(fetchLibraryOwnerOnboardingData(library.id))
      }
    })
    await Promise.all(promises)
  }

  /**
   * ==================== LIBRARY ACCOUNTS ====================
   */

  const userLibraryMemberships = ref<LibraryMembership[]>([])

  const librariesById = computed((): Record<LibraryId, Library> => userAccountsStore.librariesById)
  const wallViewableLibraryIds = computed((): LibraryId[] => userAccountsStore.wallViewableLibraryIds)
  const wallCreatableLibraryIds = computed((): LibraryId[] => userAccountsStore.wallCreatableLibraryIds)
  const currentLibrary = computed((): Library | null => {
    if (currentAccountKey.value == null) return null
    if (currentAccountKey.value.type === 'user') return null
    return librariesById.value[currentAccountKey.value.id]
  })
  const currentLibraryDashboardPath = computed((): string => {
    if (currentLibrary.value?.dashboardUrl == null) return ''
    return new URL(currentLibrary.value?.dashboardUrl).pathname
  })
  const currentLibraryId = computed((): LibraryId | null => currentLibrary.value?.id ?? null)
  const defaultLibrary = computed((): Library | undefined => {
    // TODO: replace user with currentUser
    const defaultLibraryId = user.value.defaultLibraryId
    if (defaultLibraryId === undefined) return undefined

    // Personal library currently does not exist
    const PERSONAL_LIBRARY_ID = ''
    if (defaultLibraryId === PERSONAL_LIBRARY_ID) return undefined

    return librariesById.value[defaultLibraryId]
  })
  const isCurrentUserLibraryOwner = computed((): boolean => {
    if (currentLibrary.value == null) return false
    return currentUser.value.id === currentLibrary.value.ownerId
  })
  const isSingleVisibleLibrary = computed(() => {
    return wallViewableAndVisibleLibrariesArray.value.length === 0
  })
  const wallViewableAndVisibleLibrariesArray = computed(
    (): Library[] => userAccountsStore.wallViewableAndVisibleLibrariesArray,
  )
  const wallViewableLibrariesArray = computed((): Library[] =>
    wallViewableLibraryIds.value.map((id) => librariesById.value[id]),
  )
  const wallCreatableLibrariesArray = computed((): Library[] =>
    wallCreatableLibraryIds.value.map((id) => librariesById.value[id]),
  )
  const librariesArray = computed((): Library[] => userAccountsStore.librariesArray)
  const canViewCrossLibraryGallery = computed((): boolean => {
    return librariesArray.value.every((library) => library.libraryAccess?.canViewGallery)
  })

  async function fetchWallViewableLibraries(): Promise<void> {
    await userAccountsStore.fetchWallViewableLibraries()
  }

  async function fetchWallCreatableLibraries(): Promise<void> {
    await userAccountsStore.fetchWallCreatableLibraries()
  }

  async function fetchUserLibraryMemberships(): Promise<void> {
    try {
      const response: JsonApiResponse<LibraryMembership> = await fetchJson(
        `/api/1/users/${currentUser.value.id}/libraries/memberships`,
        {
          method: HTTPMethod.get,
        },
      )
      const libraryMemberships = (response?.data as Array<JsonApiData<LibraryMembership>>).map(
        (membership) => membership.attributes,
      )
      userLibraryMemberships.value = libraryMemberships
    } catch (error) {
      genericFetchError({ error, source: 'DashAccountsStoreFetchUserLibraryMemberships' })
    }
  }

  /**
   * ==================== ONBOARDING ====================
   */

  const ownerOnboardingDataByLibraryId = ref<Record<LibraryId, OwnerOnboardingData>>({})

  const activeOwnerOnboardingData = computed((): OwnerOnboardingData | {} => {
    if (currentAccountKey.value == null || !isCurrentUserLibraryOwner.value) return {}
    return ownerOnboardingDataByLibraryId.value[currentAccountKey.value.id]
  })
  const numOwnerOnboardingTasksCompleted = computed((): number => {
    const ownerOnboardingData = activeOwnerOnboardingData.value as OwnerOnboardingData
    if (ownerOnboardingData?.tasks === undefined) return 0

    return Object.values(ownerOnboardingData.tasks).reduce(
      (total, taskCompleted) => total + (taskCompleted === false ? 0 : 1),
      0,
    )
  })
  const ownerOnboardingTasksTotal = computed((): number => {
    const ownerOnboardingData = activeOwnerOnboardingData.value as OwnerOnboardingData
    if (ownerOnboardingData?.tasks === undefined) return 0

    return Object.values(ownerOnboardingData.tasks).length
  })
  const ownerOnboardingProgressString = computed((): string => {
    if (ownerOnboardingTasksAllCompleted.value) return __('Complete!')

    return __('%{tasksCompleted} of %{total}', {
      tasksCompleted: numOwnerOnboardingTasksCompleted.value,
      total: ownerOnboardingTasksTotal.value,
    })
  })
  const ownerOnboardingTasksAllCompleted = computed(
    (): boolean => numOwnerOnboardingTasksCompleted.value === ownerOnboardingTasksTotal.value,
  )
  const currentLibraryOwnerOnboardingTasksData = computed((): OwnerOnboardingTaskData[] => {
    if (currentAccountKey.value == null) return []
    if (!isCurrentAccountLibrary.value) return []
    if (
      ownerOnboardingDataByLibraryId.value == null ||
      Object.keys(ownerOnboardingDataByLibraryId.value).length === 0 ||
      ownerOnboardingDataByLibraryId.value[currentAccountKey.value.id]?.tasks == null
    )
      return []

    const tasksArray = Object.entries(ownerOnboardingDataByLibraryId.value[currentAccountKey.value.id]?.tasks)
    const libraryType = currentLibrary.value?.libraryType as LibraryType
    const tasksDataArray: OwnerOnboardingTaskData[] = tasksArray
      .filter((taskTuple) => {
        const task = taskTuple[0]
        // Hide pick plan onboarding task for school libraries since users cannot pick school billing plans initially
        // School library owners cannot pick plans - they have to speak to Customer Success to do so
        if (task === 'pickPlan' && libraryType === LibraryType.School) {
          return false
        }

        return true
      })
      .map((taskTuple) => {
        const [task, taskCompleted] = taskTuple
        let taskData: OwnerOnboardingTaskData = getOwnerOnboardingDataByTask(task as OwnerOnboardingTask, libraryType)
        const isTaskDone = taskCompleted as boolean
        const hasTaskDoneAt = isTaskDone && typeof taskCompleted === 'string'
        taskData = {
          ...taskData,
          isTaskDone,
          taskDoneAt: hasTaskDoneAt ? taskCompleted : null,
        }
        if (taskData.key === 'pickPlan' && libraryType === LibraryType.Team) {
          return {
            ...taskData,
            taskStringOne: __('Upgrade your team'),
            taskStringTwo: __('Keep your team active when your free trial ends by upgrading to Team Gold.'),
          }
        }
        return taskData
      })

    return sortBy(tasksDataArray, 'order')
  })
  const currentAccountName = computed((): string => {
    const activeCollectionKey = dashCollectionsStore.activeCollectionKey?.indexKey as string
    if (activeCollectionKey === 'combined_recents') {
      return ''
    } else if (activeCollectionKey === 'gallery') {
      return __('Gallery')
    } else if (['combined_shared', 'combined_shared_library', 'combined_shared_user'].includes(activeCollectionKey)) {
      if (
        !isNativeAccount.value ||
        (dashCollectionsStore.sharedCollections.length === 0 && !windowSizeStore.isSmallerThanTabletPortrait)
      )
        return ''
      return __('Shared')
    } else if (dashCollectionsStore.isActiveCollectionBookmark) {
      return __('Bookmarks')
    } else if (currentAccountKey.value?.type === 'user') {
      return accountNamesByUserId.value[currentAccountKey.value.id]
    } else if (currentAccountKey.value?.type === 'library') {
      return librariesById.value[currentAccountKey.value?.id]?.name
    } else {
      return ''
    }
  })

  function isOwnerOnboardingForLibrary(libraryId: LibraryId): boolean {
    if (!isNativeAccount.value) return false
    const ownerOnboardingData = ownerOnboardingDataByLibraryId.value[libraryId]
    return ownerOnboardingData !== undefined && ownerOnboardingData.completedAt == null
  }

  function navigateToOwnerOnboardingTask(payload: { taskKey: OwnerOnboardingTask }): void {
    if (currentAccountKey.value == null || !isCurrentUserLibraryOwner.value) return

    const target = device.electronApp ? '_blank' : '_self'
    switch (payload.taskKey) {
      case 'addNewTeam': {
        break
      }
      case 'customizeTeamInfo': {
        navigateTo(`/dashboard/settings/library_info?library_id=${currentAccountKey.value.id}`, {
          target,
        })
        break
      }
      case 'addMembers': {
        navigateTo(`/dashboard/settings/library_members?library_id=${currentAccountKey.value.id}`, {
          target,
        })
        break
      }
      case 'pickPlan': {
        navigateTo(`/dashboard/settings/library_billing?library_id=${currentAccountKey.value.id}`, {
          target,
        })
        break
      }
      case 'makePrivateWall': {
        createWallOrPromptUpgrade()
        break
      }
    }
  }

  function createWallOrPromptUpgrade(): void {
    const allowWallCreation =
      currentUser.value == null || canCreateWall.value || (currentUserOptions.value.canMakeInLibrary as boolean)
    if (!allowWallCreation) {
      void personalPlansStore.quotaTriggeredUpgrade({ user: currentUser.value })
    } else {
      // List of wall-creatable libraries should have been fetched at initialization
      dashStore.switchView({ page: 'layoutPicker' })
    }
  }

  function confirmDismissOnboarding(): void {
    const afterConfirmActions = [
      dismissOnboarding,
      () => dashStore.switchView({ filter: 'all', mobilePage: MobilePage.Collection }),
    ]

    globalConfirmationDialogStore.openConfirmationDialog({
      ...ALERT_ICON,
      shouldFadeIn: true,
      title: __('Remove page?'),
      body: __('Are you sure you want to remove the Getting started page from dashboard?'),
      confirmButtonText: __('Remove'),
      cancelButtonText: __('Nevermind'),
      forceFullWidthButtons: true,
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      afterConfirmActions,
    })
  }

  async function dismissOnboarding(): Promise<void> {
    if (currentAccountKey.value?.id === undefined) return
    try {
      const response = await fetchJson(`/api/1/libraries/${currentAccountKey.value.id}/owner_onboarding/dismiss`, {
        method: HTTPMethod.post,
        csrfToken: getCsrfToken(),
      })
      setLibraryOwnerOnboardingData({
        libraryId: (response.data as JsonAPIResource<OwnerOnboardingData>).attributes.libraryId,
        ownerOnboardingData: (response.data as JsonAPIResource<OwnerOnboardingData>).attributes,
      })
    } catch (error) {
      genericFetchError({ error, source: 'DashAccountsStoreDismissOnboarding' })
    }
  }

  async function fetchLibraryOwnerOnboardingData(libraryId: LibraryId): Promise<void> {
    try {
      const response = await fetchJson(`/api/1/libraries/${libraryId}/owner_onboarding`)

      // add new team task is always completed
      const ownerOnboardingData = response.data.attributes
      ownerOnboardingData.tasks.addNewTeam = true
      setLibraryOwnerOnboardingData({ libraryId, ownerOnboardingData })
    } catch (error) {
      genericFetchError({ error, source: 'DashAccountsStoreFetchLibraryOwnerOnboardingData' })
    }
  }

  function setLibraryOwnerOnboardingData(payload: {
    libraryId: LibraryId
    ownerOnboardingData: OwnerOnboardingData
  }): void {
    ownerOnboardingDataByLibraryId.value = {
      ...ownerOnboardingDataByLibraryId.value,
      [payload.libraryId]: payload.ownerOnboardingData,
    }
  }

  return {
    /**
     * ==================== PERSONAL ACCOUNTS ====================
     */
    user,
    usersById,
    tenantsById,
    currentUserOptions,
    currentAccountKey,
    isCurrentAccountLibrary,
    currentUser,
    canCreateWall,
    isNativeAccount,
    currentTenant,
    currentTenantName,
    currentTenantLogo,
    isBackpack,
    isBriefcase,
    isStudent,
    userRolesByLibraryId,
    usersArray,
    accountNamesByUserId,
    nonCurrentTenantAccountsMenuList,
    accountsMenuList,
    transferableAccounts,
    dashNameString,
    dashGreeting,
    updateUserOptions,
    refreshWallQuotas,
    initialize,

    /**
     * ==================== LIBRARY ACCOUNTS ====================
     */

    librariesById,
    wallCreatableLibraryIds,
    wallViewableLibraryIds,
    userLibraryMemberships,
    currentLibrary,
    currentLibraryDashboardPath,
    currentLibraryId,
    defaultLibrary,
    isCurrentUserLibraryOwner,
    isSingleVisibleLibrary,
    wallViewableAndVisibleLibrariesArray,
    wallViewableLibrariesArray,
    wallCreatableLibrariesArray,
    librariesArray,
    canViewCrossLibraryGallery,
    fetchWallViewableLibraries,
    fetchWallCreatableLibraries,
    fetchUserLibraryMemberships,

    /**
     * ==================== ONBOARDING ====================
     */
    ownerOnboardingDataByLibraryId,
    activeOwnerOnboardingData,
    numOwnerOnboardingTasksCompleted,
    ownerOnboardingTasksTotal,
    ownerOnboardingProgressString,
    ownerOnboardingTasksAllCompleted,
    currentLibraryOwnerOnboardingTasksData,
    currentAccountName,
    isOwnerOnboardingForLibrary,
    navigateToOwnerOnboardingTask,
    createWallOrPromptUpgrade,
    confirmDismissOnboarding,
    dismissOnboarding,
    fetchLibraryOwnerOnboardingData,
    setLibraryOwnerOnboardingData,
  }
})
